import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { downloadHTML } from '../../services/deploy';
import PageCanvas from '../page-builder/components/PageCanvas';
import SettingsPanel from '../page-builder/components/SettingsPanel';
import SettingsDrawer from '../page-builder/components/SettingsDrawer';
import SectionLibraryModal from '../page-builder/components/SectionLibraryModal';
import ViewportSwitcher from '../page-builder/components/ViewportSwitcher';
import UndoRedoControls from '../page-builder/components/UndoRedoControls';
import Icon from '../../components/AppIcon';
import config from '../../config';

const STORAGE_KEY = 'sandboxSections';

const Sandbox = () => {
  const [sections, setSections] = useState([]);
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [currentViewport, setCurrentViewport] = useState('desktop');
  const [history, setHistory] = useState([[]]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Overlay state
  const [showLibraryModal, setShowLibraryModal] = useState(false);
  const [insertAtIndex, setInsertAtIndex] = useState(null);
  const [showSettingsDrawer, setShowSettingsDrawer] = useState(false);

  // Load from localStorage
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setSections(parsed);
        setHistory([parsed]);
      } catch (e) {
        console.error('Failed to load sandbox sections:', e);
      }
    }
  }, []);

  const addToHistory = (newSections) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newSections);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
    setHasUnsavedChanges(true);
  };

  const handleAddSection = (component, atIndex = null) => {
    const defaultContent = component?.isRegistrySection && component?.defaultData
      ? component.defaultData
      : {};

    const newSection = {
      id: `section-${Date.now()}`,
      type: component?.id,
      name: component?.name,
      content: defaultContent,
      styles: {
        paddingTop: 64,
        paddingBottom: 64,
        backgroundColor: '#FFFFFF'
      },
      visibility: {
        mobile: true,
        tablet: true,
        desktop: true
      }
    };

    let newSections;
    if (atIndex !== null && atIndex >= 0) {
      newSections = [...sections];
      newSections.splice(atIndex, 0, newSection);
    } else {
      newSections = [...sections, newSection];
    }

    setSections(newSections);
    addToHistory(newSections);
    setSelectedSectionId(newSection.id);
    setShowSettingsDrawer(true);
  };

  const handleSelectSection = (sectionId) => {
    setSelectedSectionId(sectionId);
    setShowSettingsDrawer(true);
  };

  const handleUpdateSection = (sectionId, updatedSection) => {
    if (!sectionId) {
      setSelectedSectionId(null);
      setShowSettingsDrawer(false);
      return;
    }

    const newSections = sections.map(section =>
      section.id === sectionId ? updatedSection : section
    );
    setSections(newSections);
    addToHistory(newSections);
  };

  const handleMoveSection = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= sections.length) return;
    const newSections = [...sections];
    const [movedSection] = newSections.splice(fromIndex, 1);
    newSections.splice(toIndex, 0, movedSection);
    setSections(newSections);
    addToHistory(newSections);
  };

  const handleDeleteSection = (sectionId) => {
    if (window.confirm('Удалить эту секцию?')) {
      const newSections = sections.filter(section => section.id !== sectionId);
      setSections(newSections);
      addToHistory(newSections);
      if (selectedSectionId === sectionId) {
        setSelectedSectionId(null);
        setShowSettingsDrawer(false);
      }
    }
  };

  const handleDuplicateSection = (sectionId) => {
    const sectionToDuplicate = sections.find(s => s.id === sectionId);
    if (!sectionToDuplicate) return;

    const duplicated = {
      ...JSON.parse(JSON.stringify(sectionToDuplicate)),
      id: `section-${Date.now()}`,
      name: `${sectionToDuplicate.name} (копия)`,
    };

    const index = sections.findIndex(s => s.id === sectionId);
    const newSections = [...sections];
    newSections.splice(index + 1, 0, duplicated);

    setSections(newSections);
    addToHistory(newSections);
    setSelectedSectionId(duplicated.id);
    setShowSettingsDrawer(true);
  };

  const handleInsertSection = (atIndex) => {
    setInsertAtIndex(atIndex);
    setShowLibraryModal(true);
  };

  const handleLibraryAddSection = (component) => {
    handleAddSection(component, insertAtIndex);
    setInsertAtIndex(null);
  };

  const handleUndo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setSections(history[newIndex]);
      setHasUnsavedChanges(true);
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setSections(history[newIndex]);
      setHasUnsavedChanges(true);
    }
  };

  const handleSave = () => {
    setIsSaving(true);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(sections));
      setHasUnsavedChanges(false);
      alert('Сохранено в браузере!');
    } catch (error) {
      console.error('Error saving:', error);
      alert('Ошибка сохранения');
    } finally {
      setIsSaving(false);
    }
  };

  const handleExport = () => {
    downloadHTML({ sections }, {
      title: 'My Website',
      filename: 'index.html'
    });
  };

  const selectedSection = sections.find(s => s.id === selectedSectionId);

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Sandbox Header */}
      <header className="fixed top-0 left-0 right-0 h-16 bg-card border-b border-border z-50 px-4">
        <div className="h-full flex items-center justify-between max-w-full">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="Layers" size={18} className="text-primary-foreground" />
              </div>
              <span className="font-semibold text-foreground hidden sm:block">{config.appName}</span>
            </Link>
            <div className="flex items-center gap-2 px-3 py-1 bg-warning/10 text-warning rounded-full text-sm">
              <Icon name="FlaskConical" size={14} />
              <span className="hidden sm:inline">Sandbox</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Link
              to="/login"
              className="px-4 py-2 text-sm text-text-secondary hover:text-foreground transition-colors"
            >
              Войти
            </Link>
            <Link
              to="/register"
              className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity"
            >
              Регистрация
            </Link>
          </div>
        </div>
      </header>

      {/* Info Banner */}
      <div className="mt-16 bg-muted border-b border-border px-4 py-2 text-center text-sm text-text-secondary">
        <Icon name="Info" size={14} className="inline mr-2" />
        Режим песочницы — данные хранятся только в вашем браузере.
        <Link to="/register" className="text-primary hover:underline ml-1">
          Зарегистрируйтесь
        </Link>
        , чтобы сохранять и публиковать сайты.
      </div>

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Toolbar */}
        <div className="p-4 bg-card border-b border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 flex-shrink-0">
          <div className="flex items-center gap-4 w-full sm:w-auto">
            <ViewportSwitcher
              currentViewport={currentViewport}
              onViewportChange={setCurrentViewport}
            />
            <UndoRedoControls
              canUndo={historyIndex > 0}
              canRedo={historyIndex < history.length - 1}
              onUndo={handleUndo}
              onRedo={handleRedo}
            />
            <button
              onClick={() => {
                setInsertAtIndex(null);
                setShowLibraryModal(true);
              }}
              className="inline-flex items-center gap-2 px-3 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
            >
              <Icon name="Plus" size={16} />
              <span className="hidden sm:inline">Добавить секцию</span>
            </button>
          </div>

          <div className="flex items-center gap-2 text-sm text-text-secondary">
            <Icon name="Layers" size={16} />
            <span>{sections.length} секций</span>
            {hasUnsavedChanges && (
              <span className="flex items-center gap-1 text-warning">
                <Icon name="AlertCircle" size={16} />
                <span className="hidden sm:inline">Не сохранено</span>
              </span>
            )}
          </div>
        </div>

        {/* Canvas — full width */}
        <div className="flex-1 overflow-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
          <PageCanvas
            sections={sections}
            onSelectSection={handleSelectSection}
            selectedSectionId={selectedSectionId}
            onMoveSection={handleMoveSection}
            onDeleteSection={handleDeleteSection}
            onDuplicateSection={handleDuplicateSection}
            onInsertSection={handleInsertSection}
            currentViewport={currentViewport}
          />
        </div>
      </div>

      {/* Settings Drawer overlay */}
      <SettingsDrawer
        isOpen={showSettingsDrawer && !!selectedSection}
        onClose={() => {
          setShowSettingsDrawer(false);
          setSelectedSectionId(null);
        }}
        title={selectedSection?.name || 'Настройки секции'}
      >
        <SettingsPanel
          selectedSection={selectedSection}
          onUpdateSection={handleUpdateSection}
          allSections={sections}
        />
      </SettingsDrawer>

      {/* Section Library Modal overlay */}
      <SectionLibraryModal
        isOpen={showLibraryModal}
        onClose={() => {
          setShowLibraryModal(false);
          setInsertAtIndex(null);
        }}
        onAddSection={handleLibraryAddSection}
      />

      {/* Action Bar */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
        <div className="flex items-center gap-2 px-4 py-2 bg-card rounded-full shadow-elevation-3 border border-border">
          <button
            onClick={handleSave}
            disabled={isSaving || !hasUnsavedChanges}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full hover:bg-muted transition-colors disabled:opacity-50"
          >
            <Icon name="Save" size={16} />
            <span className="hidden sm:inline">Сохранить</span>
          </button>
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full hover:bg-muted transition-colors"
          >
            <Icon name="Download" size={16} />
            <span className="hidden sm:inline">Экспорт</span>
          </button>
          <Link
            to="/register"
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-full hover:opacity-90 transition-opacity"
          >
            <Icon name="Rocket" size={16} />
            <span>Опубликовать</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Sandbox;
